<script setup>
import {
  computed,
  ref,
  reactive,
  onMounted,
  onUnmounted,
  nextTick,
  watch,
} from "vue";
import {
  Search,
  AlertTriangle,
  X,
  Package,
  Folder,
  Store,
  User,
  FolderCheck,
  FolderX,
  AlertTriangleIcon,
} from "lucide-vue-next";
import useFolders from "@/Services/FoldersServices";
import Loader from "./Loader.vue";
import moment from "moment";
import axios from "axios";

const { searchFolders, searchFoldersByParam, restore } = useFolders();

const query = ref("");
const loadingFolders = ref(false);
const showDiv = ref(false);
const searchInput = ref(null);
const filterType = reactive({
  folderNumber: true,
  CM: true,
  cuisiniste: false,
  cityOrCP: false,
  deletedFolders: false,
  matter: false,
});
const options = reactive({ div: false, searchText: false });
let cancelToken = ref(null);

const emit = defineEmits(["update:openSearch"]);
const props = defineProps({
  openSearch: {
    type: Boolean,
  },
  query: {
    type: String,
  },
});

watch(
  () => props.openSearch,
  () => {
    if (props.openSearch) {
      toggleShowDiv();
    }
  }
);

const debounce = (func, delay) => {
  let timerId;
  return (...args) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const filteredFolders = debounce(() => {
  if (query.value.length >= 3) {
    searchFolders.value = [];
    if (cancelToken.value) {
      cancelToken.value.cancel();
    }
    cancelToken.value = axios.CancelToken.source();

    loadingFolders.value = true;

    searchFoldersByParam(
      { query: query.value, filterType: filterType },
      cancelToken.value
    )
      .then(() => {
        loadingFolders.value = false;
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          loadingFolders.value = true;
        } else {
          loadingFolders.value = false;
        }
      });
  }
}, 1000);

const openWindow = (folder_id) => {
  window.open(`/planning/folders/${folder_id}`);
  if (!options.searchText) {
    query.value = "";
  }
  showDiv.value = options.div;
  searchInput.value.focus();
};

const toggleShowDiv = async () => {
  showDiv.value = !showDiv.value;
  await nextTick();
  if (showDiv.value) {
    searchInput.value.focus();
    if (props.query) {
      query.value = props.query;
      filteredFolders();
    }
  } else {
    emit("update:openSearch", false);
    query.value = "";
  }
};

const toggleOptions = (option) => {
  options[option] = !options[option];
};

const restoreFolder = async (folder) => {
  // On cache le bouton immédiatement dans l'UI
  const originalDate = folder.deleted_at;
  folder.deleted_at = null;

  try {
    await restore(folder.id);
  } catch (error) {
    // En cas d'erreur, on remet l'état précédent
    folder.deleted_at = originalDate;
    console.error("Erreur lors de la restauration", error);
  }
};
</script>

<template>
  <Transition name="bounce">
    <div
      class="fixed inset-0 bg-primary pt-24 overflow-y-scroll z-9999"
      v-if="showDiv"
    >
      <div
        class="flex flex-col md:flex-row items-center justify-center md:items-start"
      >
        <div class="mr-10 w-96">
          <div class="flex justify-end space-x-2 mx-4">
            <p class="text-xs">Recherche pouvant être plus lente</p>
            <AlertTriangleIcon size="16" class="text-orange-500" />
          </div>
          <label
            for="filterType.deletedFolders"
            class="relative flex items-start border rounded-2xl p-3 m-3"
            :class="filterType.deletedFolders ? 'bg-secondary' : ''"
          >
            <div class="flex h-6 items-center">
              <input
                id="filterType.deletedFolders"
                name="filterType.deletedFolders"
                aria-describedby="comments-description"
                type="checkbox"
                class="h-4 w-4 rounded focus:ring-primary text-tertiary"
                v-model="filterType.deletedFolders"
                @click="filteredFolders()"
              />
            </div>
            <div
              class="ml-3 mr-8 text-sm leading-6 flex items-center justify-between w-full space-x-2"
            >
              <p class="font-medium">Inclure les dossiers supprimés</p>
              <FolderX class="text-red-600" />
            </div>
            <div class="absolute top-1 right-1">
              <AlertTriangleIcon size="16" class="text-orange-500" />
            </div>
          </label>
          <label
            for="filterType.folderNumber"
            class="relative flex items-start border rounded-2xl p-3 m-3"
            :class="filterType.folderNumber ? 'bg-secondary' : ''"
          >
            <div class="flex h-6 items-center">
              <input
                id="filterType.folderNumber"
                name="filterType.folderNumber"
                aria-describedby="comments-description"
                type="checkbox"
                class="h-4 w-4 rounded focus:ring-primary text-tertiary"
                v-model="filterType.folderNumber"
                @click="filteredFolders()"
              />
            </div>
            <div class="ml-3 text-sm leading-6">
              <p class="font-medium">Numéro de dossier</p>
              <p id="comments-description" class="text-xs">
                Recherche par numéros de dossiers
              </p>
            </div>
          </label>
          <label
            for="filterType.CM"
            class="relative flex items-start border rounded-2xl p-3 m-3"
            :class="filterType.CM ? 'bg-secondary' : ''"
          >
            <div class="flex h-6 items-center">
              <input
                id="filterType.CM"
                name="filterType.CM"
                aria-describedby="comments-description"
                type="checkbox"
                class="h-4 w-4 rounded focus:ring-primary text-tertiary"
                v-model="filterType.CM"
                @click="filteredFolders()"
              />
            </div>
            <div class="ml-3 text-sm leading-6">
              <p class="font-medium">Contremarque</p>
              <p id="comments-description" class="text-xs">
                Recherche par contremarque
              </p>
            </div>
          </label>
          <label
            for="filterType.cuisiniste"
            class="relative flex items-start border rounded-2xl p-3 m-3"
            :class="filterType.cuisiniste ? 'bg-secondary' : ''"
          >
            <div class="flex h-6 items-center">
              <input
                id="filterType.cuisiniste"
                name="filterType.cuisiniste"
                aria-describedby="comments-description"
                type="checkbox"
                class="h-4 w-4 rounded focus:ring-primary text-tertiary"
                v-model="filterType.cuisiniste"
                @click="filteredFolders()"
              />
            </div>
            <div class="ml-3 text-sm leading-6">
              <p class="font-medium">Nom du cuisiniste</p>
              <p id="comments-description" class="text-xs">
                Recherche par cuisiniste
              </p>
            </div>
          </label>
          <label
            for="filterType.cityOrCP"
            class="relative relative flex items-start border rounded-2xl p-3 m-3"
            :class="filterType.cityOrCP ? 'bg-secondary' : ''"
          >
            <div class="flex h-6 items-center">
              <input
                id="filterType.cityOrCP"
                name="filterType.cityOrCP"
                aria-describedby="comments-description"
                type="checkbox"
                class="h-4 w-4 rounded focus:ring-primary text-tertiary"
                v-model="filterType.cityOrCP"
                @click="filteredFolders()"
              />
            </div>
            <div class="ml-3 text-sm leading-6">
              <p class="font-medium">Ville ou code postal</p>
              <p id="comments-description" class="text-xs">
                Recherche par ville ou code postal
              </p>
            </div>
            <div class="absolute top-1 right-1">
              <AlertTriangleIcon size="16" class="text-orange-500" />
            </div>
          </label>
          <label
            for="filterType.matter"
            class="relative relative flex items-start border rounded-2xl p-3 m-3"
            :class="filterType.matter ? 'bg-secondary' : ''"
          >
            <div class="flex h-6 items-center">
              <input
                id="filterType.matter"
                name="filterType.matter"
                aria-describedby="comments-description"
                type="checkbox"
                class="h-4 w-4 rounded focus:ring-primary text-tertiary"
                v-model="filterType.matter"
                @click="filteredFolders()"
              />
            </div>
            <div class="ml-3 text-sm leading-6">
              <p class="font-medium">Matière</p>
              <p id="comments-description" class="text-xs">
                Recherche par matière
              </p>
              <div class="absolute top-1 right-1">
                <AlertTriangleIcon size="16" class="text-orange-500" />
              </div>
            </div>
          </label>
          <label
            for="filterType.matter"
            class="relative flex items-center flex-row-reverse rounded p-1 mx-3"
          >
            <div class="flex h-6 items-center">
              <input
                id="filterType.matter"
                name="filterType.matter"
                aria-describedby="comments-description"
                type="checkbox"
                class="h-4 w-4 rounded focus:ring-primary text-tertiary"
                @click="toggleOptions('div')"
              />
            </div>
            <div class="mr-3 text-sm">
              <p class="font-medium text-xs">
                Laisser ouvert la recherche au clic
              </p>
            </div>
          </label>
          <label
            for="filterType.matter"
            class="relative flex items-center flex-row-reverse rounded p-1 mx-3"
          >
            <div class="flex h-6 items-center">
              <input
                id="filterType.matter"
                name="filterType.matter"
                aria-describedby="comments-description"
                type="checkbox"
                class="h-4 w-4 rounded focus:ring-primary text-tertiary"
                @click="toggleOptions('searchText')"
              />
            </div>
            <div class="mr-3 text-sm">
              <p class="font-medium text-xs">
                Ne pas réinitialiser la recherche au clic
              </p>
            </div>
          </label>
        </div>
        <div class="relative">
          <input
            ref="searchInput"
            type="search"
            v-model="query"
            class="text-2xl lg:text-6xl outline-none bg-transparent border-transparent focus:border-transparent focus:ring-0 border-b-4"
            placeholder="Recherche"
            @keyup="query.length > 1 && filteredFolders()"
          />
          <div
            class="flex flex-col items-center justify-center my-10"
            v-if="loadingFolders"
          >
            <Loader size="20" class="" />
          </div>
          <div
            v-if="query !== '' && searchFolders?.length == 0 && !loadingFolders"
            class="z-10 shadow"
          >
            <div class="flex items-center px-2 py-3 text-xl ring rounded mt-4">
              <AlertTriangle size="48" />
              <p class="ml-2">Aucun résultat pour "{{ query }}"</p>
            </div>
          </div>
          <div
            v-if="query.length > 1"
            id="querySearch"
            class="mt-4 z-10 text-xl overflow-auto h-[calc(70vh)]"
          >
            <div class="flex justify-end" v-if="!loadingFolders">
              <p class="inline-block text-xs border rounded px-1">
                {{ searchFolders?.length }} résultats
              </p>
            </div>
            <div
              v-for="folder in searchFolders"
              :key="folder.id"
              class="flex justify-center my-2"
            >
              <div
                @click="openWindow(folder.id)"
                class="p-2 font-semibold cursor-pointer hover:ring ring-seconndary rounded flex items-center justify-between w-11/12"
              >
                <div class="flex-1 basis-8/12">
                  <div
                    class="flex justify-start items-center flex-wrap space-x-2"
                  >
                    <FolderCheck
                      class="text-green-600"
                      v-if="!folder.deleted_at"
                    />
                    <FolderX v-else class="text-red-600" />
                    <div>{{ folder.ref }}</div>
                    <div>{{ folder.ref_client }}</div>
                  </div>
                  <div class="text-sm">
                    <div class="flex items-center space-x-2">
                      <Store size="16" />
                      <p class="mr-2 flex-1">{{ folder.society.name ?? "" }}</p>
                    </div>
                    <div class="flex items-center flex-1 space-x-2">
                      <Package size="16" />
                      <p>{{ folder.client?.full_name }}</p>
                      <p>{{ folder.client?.zip }} {{ folder.client?.town }}</p>
                    </div>
                  </div>
                </div>
                <div class="text-xs basis-2/12 flex-1 h-full">
                  <p>{{ folder.principal_product ?? "NC" }}</p>
                  <div class="bg-secondary py-0.5 px-2 rounded-lg">
                    <span class="text-xxs">Dernière intervention</span>
                    <div class="flex space-x-2">
                      <p>{{ folder.latest_intervention?.type?.initials }}</p>
                      <p>
                        {{
                          moment(folder?.latest_intervention?.start).format(
                            "DD/MM/Y"
                          )
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="folder.deleted_at" class="ml-4 transition-all flex justify-center items-center">
                <button
                  class="small-btn btn-primary flex flex-col items-center justify-center"
                  title="Restaurer ce dossier"
                  @click.stop="restoreFolder(folder)"
                >
                  <Icon
                    icon="material-symbols:settings-backup-restore"
                    width="16"
                  />
                  <span class="text-xxs font-black uppercase">Restaurer</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="absolute top-10 right-10">
        <button @click="toggleShowDiv()">
          <X size="96" class="text-primaryT" />
        </button>
      </div>
    </div>
  </Transition>
</template>

<style>
.bounce-enter-active {
  animation: bounce-in 0.25s;
}

.bounce-leave-active {
  animation: bounce-in 0.25s reverse;
}

@keyframes bounce-in {
  0% {
    transform: scale(0);
    transform-origin: top right;
  }

  100% {
    transform: scale(1);
    transform-origin: top right;
  }
}
</style>
